import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
    Asset,
    BaseDetailComponent,
    Category,
    ConfigurableOperation,
    ConfigurableOperationDefinition,
    ConfigurableOperationInput,
    CreateCollectionInput,
    createUpdatedTranslatable,
    CreateVendorCategoryInput,
    CustomFieldConfig,
    DataService,
    encodeConfigArgValue,
    findTranslation,
    getConfigArgValue,
    GetCustomerGroupWithCustomers,
    GetVendors,
    LanguageCode,
    ModalService,
    NotificationService,
    Permission,
    ServerConfigService,
    UpdateCollectionInput,
    UpdateVendorCategoryInput,
} from '@ishop/admin-ui/core';
import { normalizeString } from '@ishop/common/lib/normalize-string';
import { combineLatest } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject,  EMPTY, Observable, of } from 'rxjs';
import { VendorCategoryMemberFetchParams } from '../vendor-category-member-list/vendor-category-member-list.component';

@Component({
    selector: 'vdr-vendor-category',
    templateUrl: './vendor-category.component.html',
    styleUrls: ['./vendor-category.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VendorCategoryComponent
    extends BaseDetailComponent<Category.Fragment>
    implements OnInit, OnDestroy {
  
    detailForm: FormGroup;
    assetChanges: {   featuredAsset?: Asset } = {};
    
    readonly updatePermission = [Permission.SuperAdmin ];
     members$: Observable<GetVendors.Items[]>;
     membersTotal$: Observable<number>;
    selectedVendorIds: string[] = [];
    fetchGroupMembers$ = new BehaviorSubject<VendorCategoryMemberFetchParams>({
        skip: 0,
        take: 0,
        filterTerm: '',
    });
    private refreshActiveGroupMembers$ = new BehaviorSubject<void>(undefined);

   // @ViewChild('collectionContents') contentsComponent: CollectionContentsComponent;

    constructor(
        router: Router,
        route: ActivatedRoute,
        serverConfigService: ServerConfigService,
        private changeDetector: ChangeDetectorRef,
        protected dataService: DataService,
        private formBuilder: FormBuilder,
        private notificationService: NotificationService,
        private modalService: ModalService,
    ) {
        super(route, router, serverConfigService, dataService);
       
        this.detailForm = this.formBuilder.group({
            name: ['', Validators.required],
            slug: '',
           
        });
      
    }

    ngOnInit() {
        this.init();
        
    }

    ngOnDestroy() {
        this.destroy();
    }

    

    assetsChanged(): boolean {
        return !!Object.values(this.assetChanges).length;
    }

    /**
     * If creating a new Collection, automatically generate the slug based on the collection name.
     */
    updateSlug(nameValue: string) {
        combineLatest(this.entity$, this.languageCode$)
            .pipe(take(1))
            .subscribe(([entity, languageCode]) => {
                const slugControl = this.detailForm.get(['slug']);
                const currentTranslation = findTranslation(entity, languageCode);
                const currentSlugIsEmpty = !currentTranslation || !currentTranslation.slug;
                if (slugControl && slugControl.pristine && currentSlugIsEmpty) {
                    slugControl.setValue(normalizeString(`${nameValue}`, '-'));
                }
            });
    }
 

    create() {
        if (!this.detailForm.dirty) {
            return;
        }
        combineLatest(this.entity$, this.languageCode$)
            .pipe(
                take(1),
                mergeMap(([category, languageCode]) => {
                    const input = this.getUpdatedCollection(
                        category,
                        this.detailForm,
                        languageCode,
                    ) as CreateVendorCategoryInput;
                    
                    return this.dataService.settings.createCategory(input);
                }),
            )
            .subscribe(
                data => {
                    this.notificationService.success(_('common.notify-create-success'), {
                        entity: 'Category',
                    });
                    this.assetChanges = {};
                    this.detailForm.markAsPristine();
                    this.changeDetector.markForCheck();
                    this.router.navigate(['../', data.createVendorCategory.id], { relativeTo: this.route });
                },
                err => {
                    this.notificationService.error(_('common.notify-create-error'), {
                        entity: 'Category',
                    });
                },
            );
    }

    save() {
        combineLatest(this.entity$, this.languageCode$)
            .pipe(
                take(1),
                mergeMap(([category, languageCode]) => {
                    const input = this.getUpdatedCollection(
                        category,
                        this.detailForm,
                        languageCode,
                    ) as UpdateVendorCategoryInput;
                    return this.dataService.settings.updateCategory(input);
                }),
            )
            .subscribe(
                () => {
                    this.assetChanges = {};
                    this.detailForm.markAsPristine();
                    this.changeDetector.markForCheck();
                    this.notificationService.success(_('common.notify-update-success'), {
                        entity: 'Category',
                    });
                    
                },
                err => {
                    this.notificationService.error(_('common.notify-update-error'), {
                        entity: 'Category',
                    });
                },
            );
    }

    canDeactivate(): boolean {
        return super.canDeactivate() && !this.assetChanges.featuredAsset;
    }

    /**
     * Sets the values of the form on changes to the category or current language.
     */
    protected setFormValues(entity: Category.Fragment, languageCode: LanguageCode) {
        const currentTranslation = findTranslation(entity, languageCode);

        this.detailForm.patchValue({
            name: currentTranslation ? currentTranslation.name : '',
            slug: currentTranslation ? currentTranslation.slug : '',  
        });
 
    }

    /**
     * Given a category and the value of the form, this method creates an updated copy of the category which
     * can then be persisted to the API.
     */
    private getUpdatedCollection(
        category: Category.Fragment,
        form: FormGroup,
        languageCode: LanguageCode,
    ): CreateVendorCategoryInput | UpdateVendorCategoryInput {
        const updatedCategory = createUpdatedTranslatable({
            translatable: category,
            updatedFields: form.value, 
            languageCode,
            defaultTranslation: {
                languageCode,
                name: category.name || '',
                slug: category.slug || '',
                
            },
        });
        return {
            ...updatedCategory, 
            featuredAssetId: this.assetChanges.featuredAsset?.id,  
        };
    }

    /**
     * Maps an array of conditions or actions to the input format expected by the GraphQL API.
     */
    private mapOperationsToInputs(
        operations: ConfigurableOperation[],
        formValueOperations: any,
    ): ConfigurableOperationInput[] {
        return operations.map((o, i) => {
            return {
                code: o.code,
                arguments: Object.values(formValueOperations[i].args).map((value: any, j) => ({
                    name: o.args[j].name,
                    value: encodeConfigArgValue(value),
                })),
            };
        });
    }
}
